/**
 * Route copy for `src/routes/booking` (keys: `routes.booking.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  bookingManageApi: {
    errorStatus: 'Error {{status}}',
    couldNotReachTheServerCheck: 'Could not reach the server. Check your connection.',
  },
  manageBookingPage: {
    confirmed: 'Confirmed',
    awaitingConfirmation: 'Awaiting confirmation',
    cancelled: 'Cancelled',
    declined: 'Declined',
    done: 'Done',
    noShow: 'No-show',
    invalidManagementLink: 'Invalid management link.',
    couldNotLoadThisAppointment: 'Could not load this appointment.',
    couldNotLoadTheAvailableSlots: 'Could not load the available slots.',
    thisSlotIsNoLongerAvailable: 'This slot is no longer available.',
    reschedulingFailed: 'Rescheduling failed.',
    theUndoFailed: 'The undo failed.',
    loadingYourAppointment: 'Loading your appointment…',
    bookingNotFound: 'Booking not found',
    thisManagementLinkIsInvalidOr: 'This management link is invalid or has expired. Check the link in your confirmation email.',
    couldNotDisplayThisAppointment: 'Could not display this appointment',
    youAreManagingThisAppointmentThrough: 'You are managing this appointment through a personal, secure link.',
    yourAppointmentHasBeenRescheduledA: 'Your appointment has been rescheduled. A confirmation email has been sent to you.',
    yourAppointmentHasBeenCancelled: 'Your appointment has been cancelled.',
    appointment: 'Appointment',
    dateAndTime: 'Date and time',
    duration: 'Duration',
    durationMinutesMinutes: '{{duration_minutes}} minutes',
    locationLink: 'Location / link',
    guest: 'Guest',
    notes: 'Notes',
    addToMyCalendarIcs: 'Add to my calendar (.ics)',
    thisAppointmentHasBeenCancelled: 'This appointment has been cancelled.',
    cancelledOnVZone: 'Cancelled on {{v}} ({{zone}}).',
    reason: 'Reason:',
    reschedule: 'Reschedule',
    cancelTheAppointment: 'Cancel the appointment',
    rescheduleTheAppointment: 'Reschedule the appointment',
    chooseANewSlot: 'Choose a new slot',
    timesShownInTheTimezoneTime: 'Times shown in the {{timezone}} time zone ({{zone}}).',
    loadingAvailableSlots: 'Loading available slots…',
    noSlotIsAvailableRightNow: 'No slot is available right now. Try again later.',
    reasonOptional: 'Reason (optional)',
    aReasonForThisChange: 'A reason for this change?',
    rescheduling: 'Rescheduling…',
    confirmTheNewSlot: 'Confirm the new slot',
    back: 'Back',
    cancelThisAppointment: 'Cancel this appointment?',
    thisActionIsFinalAndFrees: 'This action is final and frees up the slot. You can book a new appointment later if needed.',
    aReasonForThisCancellation: 'A reason for this cancellation?',
    cancelling: 'Cancelling…',
    confirmTheCancellation: 'Confirm the cancellation',
    keepTheAppointment: 'Keep the appointment',
  },
  publicBookingPage: {
    couldNotLoadTheAvailableSlots: 'Could not load the available slots right now.',
    couldNotLoadThisBookingPage: 'Could not load this booking page right now.',
    yourNameIsRequired: 'Your name is required.',
    yourEmailIsRequired: 'Your email is required.',
    invalidEmail: 'Invalid email.',
    thisFieldIsRequired: 'This field is required.',
    youMustAcceptToContinue: 'You must accept to continue.',
    pleaseChooseASlot: 'Please choose a slot.',
    perThePolicy: 'per the policy',
    thisSlotIsNoLongerAvailable: 'This slot is no longer available. We refreshed the list — choose another one.',
    someInformationIsInvalidCheckThe: 'Some information is invalid. Check the form.',
    theBookingFailedTryAgainIn: 'The booking failed. Try again in a moment.',
    loadingTheBookingPage: 'Loading the booking page…',
    pageNotFoundOrInactive: 'Page not found or inactive',
    thisBookingLinkDoesNotExist: 'This booking link does not exist or no longer accepts appointments. Check the address or ask your contact for a new link.',
    thisBookingLinkIsPrivate: 'This booking link is private',
    thisPageIsOnlyAccessibleThrough: 'This page is only accessible through a personal link. Yours is missing or no longer valid. Ask your contact for a new link to book an appointment.',
    anErrorOccurred: 'An error occurred',
    tryAgain: 'Try again',
    requestReceivedAwaitingConfirmation: 'Request received — awaiting confirmation',
    bookingConfirmed: 'Booking confirmed',
    dateAndTime: 'Date and time',
    minV: 'min · {{v}}',
    meetingLink: 'Meeting link',
    location: 'Location',
    yourRequestHasBeenRecordedYou: 'Your request has been recorded. You will receive an email as soon as your contact confirms it.',
    anEmail: 'An email',
    summary: 'summary',
    confirmation: 'confirmation',
    wasSentTo: 'was sent to',
    addToMyCalendarIcs: 'Add to my calendar (.ics)',
    rescheduleOrCancel: 'Reschedule or cancel',
    min: 'min',
    upToCapacityParticipants: 'Up to {{capacity}} participants',
    n1Participant: '1 participant',
    chooseASlot: 'Choose a slot',
    chooseASlot2: 'Choose a slot',
    timesShownInYourTimeZone: 'Times shown in your time zone: {{v}}',
    loadingSlots: 'Loading slots…',
    noSlotAvailableRightNowCome: 'No slot available right now. Come back later or contact your contact person.',
    refresh: 'Refresh',
    day: 'Day',
    time: 'Time',
    yourInformation: 'Your information',
    chosenSlot: 'Chosen slot:',
    firstSelectASlotOnThe: 'First select a slot on the left.',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    notes: 'Notes',
    aTopicToDiscussOptional: 'A topic to discuss? (optional)',
    iAgree: 'I agree.',
    confirming: 'Confirming…',
    confirmTheBooking: 'Confirm the booking',
    byConfirmingYouAgreeToBe: 'By confirming, you agree to be contacted about this appointment.',
    choose: '— Choose —',
  },
};

export default en;
